import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Navbar from '../components/Layout/Navbar';
import Footerpage from '../components/Layout/Footer';
import './BrochureDownload.css';

const BrochureDownload = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloadStarted, setDownloadStarted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setDownloadStarted(true);
      
      // Create a dummy PDF blob and trigger download
      // For now, we'll create a simple text file as a placeholder
      const dummyContent = `
WeIntern - 2 Month Live Internship Program
Official Brochure 2024

Student Details:
Name: ${formData.name}
Email: ${formData.email}
Phone: ${formData.phone}
Domain: ${formData.course}

Program Highlights:
✓ 100% Live Interactive Classes
✓ Real Industry Projects
✓ Official Certification
✓ Placement Support
✓ Resume Building & Interview Prep
✓ LinkedIn Profile Optimization

For more information, visit: www.we-intern.in
Contact: contact@we-intern.in
      `;
      
      const blob = new Blob([dummyContent], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'WeIntern-2Month-Internship-Brochure.txt';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      // Reset form after 2 seconds
      setTimeout(() => {
        setFormData({ name: '', email: '', phone: '', course: '' });
        setDownloadStarted(false);
      }, 2000);
    }, 1500);
  };

  return (
    <div className="brochure-page">
      <Navbar />
      
      {/* Back to Site Button */}
      <div className="back-to-site-container">
        <button className="back-to-site-btn" onClick={() => navigate('/internship')}>
          <Icon icon="mdi:arrow-left" width={20} />
          Back to Site
        </button>
      </div>
      
      <div className="brochure-container">
        {/* Left Side - Form */}
        <div className="brochure-left">
          <div className="brochure-header">
            <div className="brochure-badge">
              <Icon icon="mdi:file-download-outline" width={18} />
              Download Brochure
            </div>
            <h1 className="brochure-title">
              Get Complete Program Details
            </h1>
            <p className="brochure-subtitle">
              Download our comprehensive brochure to learn about the 2 or 4 month internship program, curriculum, certification, and career opportunities.
            </p>
          </div>

          {!downloadStarted ? (
            <form onSubmit={handleSubmit} className="brochure-form">
              <div className="form-group">
                <label>Full Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your full name"
                  required
                />
              </div>

              <div className="form-group">
                <label>Email Address *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div className="form-group">
                <label>Phone Number *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter your phone number"
                  pattern="[0-9]{10}"
                  required
                />
              </div>

              <div className="form-group">
                <label>Interested Domain *</label>
                <select
                  name="course"
                  value={formData.course}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a domain</option>
                  <option value="aiml">AI/ML Internship</option>
                  <option value="webdev">Web Development</option>
                  <option value="data-analytics">Data Analytics</option>
                  <option value="data-science">Data Science</option>
                  <option value="devops">DevOps</option>
                  <option value="uiux">UI/UX Design</option>
                </select>
              </div>

              <button 
                type="submit" 
                className="brochure-submit-btn"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Icon icon="mdi:loading" width={20} className="spinning" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Icon icon="mdi:download" width={20} />
                    Download Brochure
                  </>
                )}
              </button>

              <p className="brochure-privacy">
                <Icon icon="mdi:lock-outline" width={16} />
                We respect your privacy. Your information is secure with us.
              </p>
            </form>
          ) : (
            <div className="download-success">
              <div className="success-icon">
                <Icon icon="mdi:check-circle" width={60} />
              </div>
              <h3>Download Started!</h3>
              <p>Your brochure is being downloaded. Check your downloads folder.</p>
              <div className="success-animation">
                <Icon icon="mdi:file-pdf-box" width={80} />
              </div>
            </div>
          )}
        </div>

        {/* Right Side - Preview */}
        <div className="brochure-right">
          <div className="brochure-preview">
            <div className="preview-badge">
              <Icon icon="mdi:eye-outline" width={18} />
              Preview
            </div>
            
            <div className="brochure-cover">
              <div className="cover-overlay">
                <img src="/logo.png" alt="WeIntern" className="logo-image" />
                <p>2 or 4 Month Live Internship Program</p>
                <div className="cover-badge">Official Brochure 2024</div>
              </div>
            </div>

            <div className="brochure-features">
              <h3>What's Inside?</h3>
              <div className="feature-list">
                <div className="feature-item">
                  <Icon icon="mdi:checkbox-marked-circle" width={22} />
                  <span>Complete Program Curriculum</span>
                </div>
                <div className="feature-item">
                  <Icon icon="mdi:checkbox-marked-circle" width={22} />
                  <span>Domain-wise Breakdown</span>
                </div>
                <div className="feature-item">
                  <Icon icon="mdi:checkbox-marked-circle" width={22} />
                  <span>Certification Details</span>
                </div>
                <div className="feature-item">
                  <Icon icon="mdi:checkbox-marked-circle" width={22} />
                  <span>Fee Structure & Refund Policy</span>
                </div>
                <div className="feature-item">
                  <Icon icon="mdi:checkbox-marked-circle" width={22} />
                  <span>Career Support & Placement</span>
                </div>
                <div className="feature-item">
                  <Icon icon="mdi:checkbox-marked-circle" width={22} />
                  <span>Success Stories & Reviews</span>
                </div>
              </div>
            </div>

            <div className="brochure-stats">
              <div className="stat-item">
                <div className="stat-icon">
                  <Icon icon="mdi:account-group" width={28} />
                </div>
                <div>
                  <h4>20,000+</h4>
                  <p>Students Enrolled</p>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">
                  <Icon icon="mdi:star" width={28} />
                </div>
                <div>
                  <h4>4.8/5</h4>
                  <p>Average Rating</p>
                </div>
              </div>
              <div className="stat-item">
                <div className="stat-icon">
                  <Icon icon="mdi:certificate" width={28} />
                </div>
                <div>
                  <h4>100%</h4>
                  <p>Certificate Guaranteed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footerpage />
    </div>
  );
};

export default BrochureDownload;
